import React from 'react';
import { Footnote, Anchor } from 'Components/core';

export default {
  PPO: [
    <p>
      With a Preferred Provider Organization (PPO), you can go to almost any
      doctor or hospital and you’re covered — giving you more choices and
      flexibility. You get special rates for doctors in your plan, which lowers
      your out-of-pocket costs.
    </p>,
    ,
    <p>PPO highlights</p>,
    <ul>
      <li>
        You can choose a primary care provider (PCP) from the plan for
        preventive care, like checkups and screenings.
      </li>
      <li>You don’t need to have a PCP to see a specialist.</li>
      <li>
        When you want to see a specialist, like an orthopedic doctor or a
        cardiologist, you don’t need to visit your PCP first to get a referral.
        This can save you time and a copay.
      </li>
      <li>You’ll pay less if you use doctors who are part of the PPO.</li>
      <li>
        You can see providers who aren’t part of the PPO, but you’ll pay more.
      </li>
      <li>
        Once you pay your deductible, you’ll pay a percentage of the total cost
        (also called coinsurance) anytime you get care for a covered service.
        Your plan will cover the rest.
      </li>
    </ul>
  ],
  HMO: [
    <p>
      The Health Maintenance Organization (HMO) plan covers services from
      doctors in your plan.
    </p>,
    <ul>
      <li>
        You can choose a primary care doctor (PCP) from the plan for preventive
        care like checkups and screenings.
      </li>
      <li>
        When you want to see a specialist, like an orthopedic doctor or a
        cardiologist, you don’t need to visit your PCP first to get a referral.
        This can save you time and a copay.
      </li>
    </ul>
  ],
  'Open Access HMO': [
    <p>
     The Open Access Health Maintenance Organization (HMO) covers services from doctors and hospitals in your plan.
    </p>,
    <ul>
      <li>
      You can choose a primary care doctor (PCP) from the plan for preventive care like checkups and screenings.
      </li>
      <li>
      When you want to see a specialist, like an orthopedic doctor or a cardiologist, you don’t need to visit your PCP first to get a referral. This can save you time and a copay.
      </li>
    </ul>
  ],
  EPO: [
    <p>
      This plan covers services from doctors and hospitals that are part of the
      Exclusive Provider Organization (EPO). You have limited coverage outside
      of the plan, but it is a more affordable option.
    </p>,
    <ul>
      <li>
        You’ll choose a primary care doctor (PCP) from the plan for preventive
        care like checkups and screenings.
      </li>
      <li>
        You don’t need a referral from your PCP to see a specialist, like an
        orthopedic doctor or a cardiologist, as long as they are in your plan.
      </li>
      <li>If you see a doctor outside the plan, you may pay more for care.</li>
    </ul>
  ],
  FSA: [
    <p>
      Your FSA lets you set aside pre-tax dollars to pay for qualified medical
      expenses. When you select your benefits, you decide how much to put in
      your FSA for the year. Your employer will deduct an equal amount from each
      paycheck to add to your FSA.
    </p>,
    ,
    <ul>
      <li>
        Money put into your FSA is tax free, which saves you money on your
        out-of-pocket costs.
      </li>
      <li>
        Your funds don’t roll over from year to year, so you’ll need to use the
        money in your account before the end of the plan year.
      </li>
      <li>
        You can use your FSA to help pay for expenses like medicine, your
        deductible, and dental and vision care.<sup>1,2</sup>
      </li>
    </ul>,
    ,
    <Footnote>
      1 For a full list of qualified expenses for an individual, visit{' '}
      <Anchor to="https://irs.gov/pub502">irs.gov/pub502</Anchor>
    </Footnote>,
    <Footnote>
      2 For a full list of qualified expenses for a family, visit{' '}
      <Anchor to="https://irs.gov/pub503">irs.gov/pub503</Anchor>
    </Footnote>
  ],
  'DC FSA': [
    <p>
      Your FSA lets you set aside pre-tax dollars to pay for qualified medical
      expenses. When you select your benefits, you decide how much to put in
      your FSA for the year. Your employer will deduct an equal amount from each
      paycheck to add to your FSA.
    </p>,
    ,
    <ul>
      <li>
        Money put into your FSA is tax free, which saves you money on your
        out-of-pocket costs.
      </li>
      <li>
        You will have access to your FSA funds as soon as your account is
        active.
      </li>
      <li>
        Your funds don’t roll over from year to year, so you’ll need to use the
        money in your account before the end of the plan year.
      </li>
    </ul>,
    ,
    <p>
      You can use your FSA to help pay for dependent care costs like daycare,
      preschool, before- and after-school care, summer camp and elder care.<sup>1, 2</sup>
    </p>,
    ,
    <Footnote>
      <sup>1</sup> For a full list of qualified expenses visit{' '}
      <Anchor to="https://irs.gov/pub502">irs.gov/pub502</Anchor>
    </Footnote>,
    <Footnote>
      <sup>2</sup> For a full list of qualified expenses visit{' '}
      <Anchor to="https://irs.gov/pub503">irs.gov/pub503</Anchor>
    </Footnote>
  ],
  HSA1: [
    <p>
      An HSA allows you to set aside pre-tax dollars to pay for care when you
      need it, now or in the future. You can use money in the account to pay for
      qualified medical expenses like hospital visits, prescription drugs or
      copays for doctor visit.<sup>1</sup> You’ll receive a debit card that you
      can use at your doctor’s office or the pharmacy to pay your share of
      costs. The money will come directly out of your HSA.
    </p>,
    <ul>
      <li>
        You’ll have a spending account with our HSA partner. They’ll send you a
        debit card that you can use at your doctor’s office or pharmacy to pay
        your share of costs. The money comes directly out of your HSA.
      </li>
      <li>
        Once you pay your deductible, you’ll pay a percentage of the total cost
        (also called coinsurance) anytime you get care for a covered service.
      </li>
      <li>
        All the money in your HSA rolls over from year to year, and it’s yours
        even if you change health plans, jobs or retire.
      </li>
      <li>
        The money you put into your HSA, any interest you earn and even the
        money you take out to pay for health care is all tax-free.
      </li>
      <li>
        You can contribute up to $3,550 for individuals and $7,100 for families.
        <sup>1</sup> This includes your employer contribution.
      </li>
      <li>If you’re 55 or older you can contribute an extra $1,000 a year.</li>
    </ul>,
    ,
    <Footnote>
      1 For a full list of qualified expenses visit{' '}
      <Anchor to="https://irs.gov/pub502">irs.gov/pub502</Anchor>.
    </Footnote>
  ],
  HSA2: [
    <p>
      An HSA allows you to set aside pre-tax dollars to pay for care when you
      need it, now or in the future. You can use money in the account to pay for
      qualified medical expenses like hospital visits, prescription drugs or
      copays for doctor visit.<sup>1</sup> You’ll receive a debit card that you
      can use at your doctor’s office or the pharmacy to pay your share of
      costs. The money will come directly out of your HSA.
    </p>,
    <ul>
      <li>
        You’ll have a spending account with our HSA partner. They’ll send you a
        debit card that you can use at your doctor’s office or pharmacy to pay
        your share of costs. The money comes directly out of your HSA.
      </li>
      <li>
        Once you pay your deductible, you’ll pay a percentage of the total cost
        (also called coinsurance) anytime you get care for a covered service.
      </li>
      <li>
        All the money in your HSA rolls over from year to year, and it’s yours
        even if you change health plans, jobs or retire.
      </li>
      <li>
        The money you put into your HSA, any interest you earn and even the
        money you take out to pay for health care is all tax-free.
      </li>
      <li>
        You can contribute up to $3,550 for individuals and $7,100 for families.
        <sup>1</sup> This includes your employer contribution.
      </li>
      <li>If you’re 55 or older you can contribute an extra $1,000 a year.</li>
      <li>
        You can use your Limited Purpose FSA to help pay for dental and vision
        care like fillings, braces, eyeglasses and contacts. You must have a
        Health Savings Account (HSA) to participate.
      </li>
    </ul>,
    ,
    <Footnote>
      1 For a full list of qualified expenses visit{' '}
      <Anchor to="https://irs.gov/pub502">irs.gov/pub502</Anchor>.
    </Footnote>
  ],
  HSA3: [
    <p>
      An HSA allows you to set aside pre-tax dollars to pay for care when you
      need it, now or in the future. You can use money in the account to pay for
      qualified medical expenses like hospital visits, prescription drugs or
      copays for doctor visit.<sup>1</sup> You’ll receive a debit card that you
      can use at your doctor’s office or the pharmacy to pay your share of
      costs. The money will come directly out of your HSA.
    </p>,
    <ul>
      <li>
        You’ll have a spending account with our HSA partner. They’ll send you a
        debit card that you can use at your doctor’s office or pharmacy to pay
        your share of costs. The money comes directly out of your HSA.
      </li>
      <li>
        Once you pay your deductible, you’ll pay a percentage of the total cost
        (also called coinsurance) anytime you get care for a covered service.
      </li>
      <li>
        All the money in your HSA rolls over from year to year, and it’s yours
        even if you change health plans, jobs or retire.
      </li>
      <li>
        The money you put into your HSA, any interest you earn and even the
        money you take out to pay for health care is all tax-free.
      </li>
      <li>
        You can contribute up to $3,550 for individuals and $7,100 for families.
        <sup>1</sup>
      </li>
      <li>If you’re 55 or older you can contribute an extra $1,000 a year.</li>
    </ul>,
    ,
    <Footnote>
      1 For a full list of qualified expenses visit{' '}
      <Anchor to="https://irs.gov/pub502">irs.gov/pub502</Anchor>.
    </Footnote>
  ],
  HSA4: [
    <p>
      An HSA allows you to set aside pre-tax dollars to pay for care when you
      need it, now or in the future. You can use money in the account to pay for
      qualified medical expenses like hospital visits, prescription drugs or
      copays for doctor visit.<sup>1</sup> You’ll receive a debit card that you
      can use at your doctor’s office or the pharmacy to pay your share of
      costs. The money will come directly out of your HSA.
    </p>,
    <ul>
      <li>
        You’ll have a spending account with our HSA partner. They’ll send you a
        debit card that you can use at your doctor’s office or pharmacy to pay
        your share of costs. The money comes directly out of your HSA.
      </li>
      <li>
        Once you pay your deductible, you’ll pay a percentage of the total cost
        (also called coinsurance) anytime you get care for a covered service.
      </li>
      <li>
        All the money in your HSA rolls over from year to year, and it’s yours
        even if you change health plans, jobs or retire.
      </li>
      <li>
        The money you put into your HSA, any interest you earn and even the
        money you take out to pay for health care is all tax-free.
      </li>
      <li>
        You can contribute up to $3,550 for individuals and $7,100 for families.
        <sup>1</sup>
      </li>
      <li>If you’re 55 or older you can contribute an extra $1,000 a year.</li>
      <li>
        You can use your Limited Purpose FSA to help pay for dental and vision
        care like fillings, braces, eyeglasses and contacts. You must have a
        Health Savings Account (HSA) to participate.
      </li>
    </ul>,
    ,
    <Footnote>
      1 For a full list of qualified expenses visit{' '}
      <Anchor to="https://irs.gov/pub502">irs.gov/pub502</Anchor>.
    </Footnote>
  ],
  HSA5: [
    <p>
      An HSA allows you to set aside pre-tax dollars to pay for care when you
      need it, now or in the future. You can use money in the account to pay for
      qualified medical expenses like hospital visits, prescription drugs or
      copays for doctor visit.<sup>1</sup> You’ll receive a debit card that you
      can use at your doctor’s office or the pharmacy to pay your share of
      costs. The money will come directly out of your HSA.
    </p>,
    <ul>
      <li>
        You’ll have a spending account with our HSA partner. They’ll send you a
        debit card that you can use at your doctor’s office or pharmacy to pay
        your share of costs. The money comes directly out of your HSA.
      </li>
      <li>
        Once you pay your deductible, you’ll pay a percentage of the total cost
        (also called coinsurance) anytime you get care for a covered service.
      </li>
      <li>
        All the money in your HSA rolls over from year to year, and it’s yours
        even if you change health plans, jobs or retire.
      </li>
      <li>
        The money you put into your HSA, any interest you earn and even the
        money you take out to pay for health care is all tax-free.
      </li>
      <li>
        You can contribute up to $3,550 for individuals and $7,100 for families.
        <sup>1</sup> This includes your employer contribution.
      </li>
      <li>
        You can earn extra money for your HSA by doing healthy activities. Your
        employer will let you know how to participate.
      </li>
      <li>If you’re 55 or older you can contribute an extra $1,000 a year.</li>
    </ul>,
    ,
    <Footnote>
      1 For a full list of qualified expenses visit{' '}
      <Anchor to="https://irs.gov/pub502">irs.gov/pub502</Anchor>.
    </Footnote>,
    ,
  ],
  HSA6: [
    <p>
      An HSA allows you to set aside pre-tax dollars to pay for care when you
      need it, now or in the future. You can use money in the account to pay for
      qualified medical expenses like hospital visits, prescription drugs or
      copays for doctor visit.<sup>1</sup> You’ll receive a debit card that you
      can use at your doctor’s office or the pharmacy to pay your share of
      costs. The money will come directly out of your HSA.
    </p>,
    <ul>
      <li>
        You’ll have a spending account with our HSA partner. They’ll send you a
        debit card that you can use at your doctor’s office or pharmacy to pay
        your share of costs. The money comes directly out of your HSA.
      </li>
      <li>
        Once you pay your deductible, you’ll pay a percentage of the total cost
        (also called coinsurance) anytime you get care for a covered service.
      </li>
      <li>
        All the money in your HSA rolls over from year to year, and it’s yours
        even if you change health plans, jobs or retire.
      </li>
      <li>
        The money you put into your HSA, any interest you earn and even the
        money you take out to pay for health care is all tax-free.
      </li>
      <li>
        You can contribute up to $3,550 for individuals and $7,100 for families.
        <sup>1</sup> This includes your employer contribution.
      </li>
      <li>
        You can earn extra money for your HSA by doing healthy activities. Your
        employer will let you know how to participate.
      </li>
      <li>If you’re 55 or older you can contribute an extra $1,000 a year.</li>
      <li>
        You can use your Limited Purpose FSA to help pay for dental and vision
        care like fillings, braces, eyeglasses and contacts. You must have a
        Health Savings Account (HSA) to participate.
      </li>
    </ul>,
    ,
    <Footnote>
      1 For a full list of qualified expenses visit{' '}
      <Anchor to="https://irs.gov/pub502">irs.gov/pub502</Anchor>.
    </Footnote>,
    ,
  ],
  HSA7: [
    <p>
      An HSA allows you to set aside pre-tax dollars to pay for care when you
      need it, now or in the future. You can use money in the account to pay for
      qualified medical expenses like hospital visits, prescription drugs or
      copays for doctor visit.<sup>1</sup> You’ll receive a debit card that you
      can use at your doctor’s office or the pharmacy to pay your share of
      costs. The money will come directly out of your HSA.
    </p>,
    <ul>
      <li>
        You’ll have a spending account with our HSA partner. They’ll send you a
        debit card that you can use at your doctor’s office or pharmacy to pay
        your share of costs. The money comes directly out of your HSA.
      </li>
      <li>
        Once you pay your deductible, you’ll pay a percentage of the total cost
        (also called coinsurance) anytime you get care for a covered service.
      </li>
      <li>
        All the money in your HSA rolls over from year to year, and it’s yours
        even if you change health plans, jobs or retire.
      </li>
      <li>
        The money you put into your HSA, any interest you earn and even the
        money you take out to pay for health care is all tax-free.
      </li>
      <li>
        You can contribute up to $3,550 for individuals and $7,100 for families.
        <sup>1</sup>
      </li>
      <li>
        You can earn extra money for your HSA by doing healthy activities. Your
        employer will let you know how to participate.
      </li>
      <li>If you’re 55 or older you can contribute an extra $1,000 a year.</li>
    </ul>,
    ,
    ,
    <Footnote>
      1 For a full list of qualified expenses visit{' '}
      <Anchor to="https://irs.gov/pub502">irs.gov/pub502</Anchor>.
    </Footnote>,
    ,
  ],
  HSA8: [
    <p>
      An HSA allows you to set aside pre-tax dollars to pay for care when you
      need it, now or in the future. You can use money in the account to pay for
      qualified medical expenses like hospital visits, prescription drugs or
      copays for doctor visit.<sup>1</sup> You’ll receive a debit card that you
      can use at your doctor’s office or the pharmacy to pay your share of
      costs. The money will come directly out of your HSA.
    </p>,
    <ul>
      <li>
        You’ll have a spending account with our HSA partner. They’ll send you a
        debit card that you can use at your doctor’s office or pharmacy to pay
        your share of costs. The money comes directly out of your HSA.
      </li>
      <li>
        Once you pay your deductible, you’ll pay a percentage of the total cost
        (also called coinsurance) anytime you get care for a covered service.
      </li>
      <li>
        All the money in your HSA rolls over from year to year, and it’s yours
        even if you change health plans, jobs or retire.
      </li>
      <li>
        The money you put into your HSA, any interest you earn and even the
        money you take out to pay for health care is all tax-free.
      </li>
      <li>
        You can contribute up to $3,550 for individuals and $7,100 for families.
        <sup>1</sup>
      </li>
      <li>
        You can earn extra money for your HSA by doing healthy activities. Your
        employer will let you know how to participate.
      </li>
      <li>If you’re 55 or older you can contribute an extra $1,000 a year.</li>
      <li>
        You can use your Limited Purpose FSA to help pay for dental and vision
        care like fillings, braces, eyeglasses and contacts. You must have a
        Health Savings Account (HSA) to participate.
      </li>
    </ul>,
    ,
    <Footnote>
      1 For a full list of qualified expenses visit{' '}
      <Anchor to="https://irs.gov/pub502">irs.gov/pub502</Anchor>.
    </Footnote>
  ],
  HSA9: [
    <p>
      An HSA allows you to set aside pre-tax dollars to pay for care when you
      need it, now or in the future. You can use money in the account to pay for
      qualified medical expenses like hospital visits, prescription drugs or
      copays for doctor visit.<sup>1</sup> You’ll receive a debit card that you
      can use at your doctor’s office or the pharmacy to pay your share of
      costs. The money will come directly out of your HSA.
    </p>,
    <ul>
      <li>
        Once you pay your deductible, you’ll pay a percentage of the total cost
        (also called coinsurance) anytime you get care for a covered service.
      </li>
      <li>
        All the money in your HSA rolls over from year to year, and it’s yours
        even if you change health plans, jobs or retire.
      </li>
      <li>
        The money you put into your HSA, any interest you earn and even the
        money you take out to pay for health care is all tax-free.
      </li>
      <li>
        You can contribute up to $3,550 for individuals and $7,100 for families.
        <sup>1</sup> This includes your employer contribution.
      </li>
      <li>If you’re 55 or older you can contribute an extra $1,000 a year.</li>
    </ul>,
    ,
    <Footnote>
      1 For a full list of qualified expenses visit{' '}
      <Anchor to="https://irs.gov/pub502">irs.gov/pub502</Anchor>.
    </Footnote>,
    ,
  ],
  HSA10: [
    <p>
      An HSA allows you to set aside pre-tax dollars to pay for care when you
      need it, now or in the future. You can use money in the account to pay for
      qualified medical expenses like hospital visits, prescription drugs or
      copays for doctor visit.<sup>1</sup> You’ll receive a debit card that you
      can use at your doctor’s office or the pharmacy to pay your share of
      costs. The money will come directly out of your HSA.
    </p>,
    <ul>
      <li>
        Once you pay your deductible, you’ll pay a percentage of the total cost
        (also called coinsurance) anytime you get care for a covered service.
      </li>
      <li>
        All the money in your HSA rolls over from year to year, and it’s yours
        even if you change health plans, jobs or retire.
      </li>
      <li>
        The money you put into your HSA, any interest you earn and even the
        money you take out to pay for health care is all tax-free.
      </li>
      <li>
        You can contribute up to $3,550 for individuals and $7,100 for families.
        <sup>1</sup> This includes your employer contribution.
      </li>
      <li>If you’re 55 or older you can contribute an extra $1,000 a year.</li>
      <li>
        You can use your Limited Purpose FSA to help pay for dental and vision
        care like fillings, braces, eyeglasses and contacts. You must have a
        Health Savings Account (HSA) to participate.
      </li>
    </ul>,
    ,
    <Footnote>
      1 For a full list of qualified expenses visit{' '}
      <Anchor to="https://irs.gov/pub502">irs.gov/pub502</Anchor>.
    </Footnote>,
    ,
  ],
  HSA11: [
    <p>
      An HSA allows you to set aside pre-tax dollars to pay for care when you
      need it, now or in the future. You can use money in the account to pay for
      qualified medical expenses like hospital visits, prescription drugs or
      copays for doctor visit.<sup>1</sup> You’ll receive a debit card that you
      can use at your doctor’s office or the pharmacy to pay your share of
      costs. The money will come directly out of your HSA.
    </p>,
    <ul>
      <li>
        Once you pay your deductible, you’ll pay a percentage of the total cost
        (also called coinsurance) anytime you get care for a covered service.
      </li>
      <li>
        All the money in your HSA rolls over from year to year, and it’s yours
        even if you change health plans, jobs or retire.
      </li>
      <li>
        The money you put into your HSA, any interest you earn and even the
        money you take out to pay for health care is all tax-free.
      </li>
      <li>
        You can contribute up to $3,550 for individuals and $7,100 for families.
        <sup>1</sup>
      </li>
      <li>If you’re 55 or older you can contribute an extra $1,000 a year.</li>
    </ul>,
    ,
    <Footnote>
      1 For a full list of qualified expenses visit{' '}
      <Anchor to="https://irs.gov/pub502">irs.gov/pub502</Anchor>.
    </Footnote>,
    ,
  ],
  HSA12: [
    <p>
      An HSA allows you to set aside pre-tax dollars to pay for care when you
      need it, now or in the future. You can use money in the account to pay for
      qualified medical expenses like hospital visits, prescription drugs or
      copays for doctor visit.<sup>1</sup> You’ll receive a debit card that you
      can use at your doctor’s office or the pharmacy to pay your share of
      costs. The money will come directly out of your HSA.
    </p>,
    <ul>
      <li>
        Once you pay your deductible, you’ll pay a percentage of the total cost
        (also called coinsurance) anytime you get care for a covered service.
      </li>
      <li>
        All the money in your HSA rolls over from year to year, and it’s yours
        even if you change health plans, jobs or retire.
      </li>
      <li>
        The money you put into your HSA, any interest you earn and even the
        money you take out to pay for health care is all tax-free.
      </li>
      <li>
        You can contribute up to $3,550 for individuals and $7,100 for families.
        <sup>1</sup>
      </li>
      <li>If you’re 55 or older you can contribute an extra $1,000 a year.</li>
      <li>
        You can use your Limited Purpose FSA to help pay for dental and vision
        care like fillings, braces, eyeglasses and contacts. You must have a
        Health Savings Account (HSA) to participate.
      </li>
    </ul>,
    ,
    <Footnote>
      1 For a full list of qualified expenses visit{' '}
      <Anchor to="https://irs.gov/pub502">irs.gov/pub502</Anchor>.
    </Footnote>,
    ,
  ],
  HSA13: [
    <p>
      An HSA allows you to set aside pre-tax dollars to pay for care when you
      need it, now or in the future. You can use money in the account to pay for
      qualified medical expenses like hospital visits, prescription drugs or
      copays for doctor visit.<sup>1</sup> You’ll receive a debit card that you
      can use at your doctor’s office or the pharmacy to pay your share of
      costs. The money will come directly out of your HSA.
    </p>,
    <ul>
      <li>
        Once you pay your deductible, you’ll pay a percentage of the total cost
        (also called coinsurance) anytime you get care for a covered service.
      </li>
      <li>
        All the money in your HSA rolls over from year to year, and it’s yours
        even if you change health plans, jobs or retire.
      </li>
      <li>
        The money you put into your HSA, any interest you earn and even the
        money you take out to pay for health care is all tax-free.
      </li>
      <li>
        You can contribute up to $3,550 for individuals and $7,100 for families.
        <sup>1</sup> This includes your employer contribution.
      </li>
      <li>
        You can earn extra money for your HSA by doing healthy activities. Your
        employer will let you know how to participate.
      </li>
      <li>If you’re 55 or older you can contribute an extra $1,000 a year.</li>
    </ul>,
    ,
    <Footnote>
      1 For a full list of qualified expenses visit{' '}
      <Anchor to="https://irs.gov/pub502">irs.gov/pub502</Anchor>.
    </Footnote>,
    ,
  ],
  HSA14: [
    <p>
      An HSA allows you to set aside pre-tax dollars to pay for care when you
      need it, now or in the future. You can use money in the account to pay for
      qualified medical expenses like hospital visits, prescription drugs or
      copays for doctor visit.<sup>1</sup> You’ll receive a debit card that you
      can use at your doctor’s office or the pharmacy to pay your share of
      costs. The money will come directly out of your HSA.
    </p>,
    <ul>
      <li>
        Once you pay your deductible, you’ll pay a percentage of the total cost
        (also called coinsurance) anytime you get care for a covered service.
      </li>
      <li>
        All the money in your HSA rolls over from year to year, and it’s yours
        even if you change health plans, jobs or retire.
      </li>
      <li>
        The money you put into your HSA, any interest you earn and even the
        money you take out to pay for health care is all tax-free.
      </li>
      <li>
        You can contribute up to $3,550 for individuals and $7,100 for families.
        <sup>1</sup> This includes your employer contribution.
      </li>
      <li>
        You can earn extra money for your HSA by doing healthy activities. Your
        employer will let you know how to participate.
      </li>
      <li>If you’re 55 or older you can contribute an extra $1,000 a year.</li>
      <li>
        You can use your Limited Purpose FSA to help pay for dental and vision
        care like fillings, braces, eyeglasses and contacts. You must have a
        Health Savings Account (HSA) to participate.
      </li>
    </ul>,
    ,
    <Footnote>
      1 For a full list of qualified expenses visit{' '}
      <Anchor to="https://irs.gov/pub502">irs.gov/pub502</Anchor>.
    </Footnote>,
    ,
  ],
  HSA15: [
    <p>
      An HSA allows you to set aside pre-tax dollars to pay for care when you
      need it, now or in the future. You can use money in the account to pay for
      qualified medical expenses like hospital visits, prescription drugs or
      copays for doctor visit.<sup>1</sup> You’ll receive a debit card that you
      can use at your doctor’s office or the pharmacy to pay your share of
      costs. The money will come directly out of your HSA.
    </p>,
    ,
    <ul>
      <li>
        Once you pay your deductible, you’ll pay a percentage of the total cost
        (also called coinsurance) anytime you get care for a covered service.
      </li>
      <li>
        All the money in your HSA rolls over from year to year, and it’s yours
        even if you change health plans, jobs or retire.
      </li>
      <li>
        The money you put into your HSA, any interest you earn and even the
        money you take out to pay for health care is all tax-free.
      </li>
      <li>
        You can contribute up to $3,550 for individuals and $7,100 for families.
        <sup>1</sup>
      </li>
      <li>
        You can earn extra money for your HSA by doing healthy activities. Your
        employer will let you know how to participate.
      </li>
      <li>If you’re 55 or older you can contribute an extra $1,000 a year.</li>
    </ul>,
    ,
    <Footnote>
      1 For a full list of qualified expenses visit{' '}
      <Anchor to="https://irs.gov/pub502">irs.gov/pub502</Anchor>.
    </Footnote>,
    ,
  ],
  HSA16: [
    <p>
      An HSA allows you to set aside pre-tax dollars to pay for care when you
      need it, now or in the future. You can use money in the account to pay for
      qualified medical expenses like hospital visits, prescription drugs or
      copays for doctor visit.<sup>1</sup> You’ll receive a debit card that you
      can use at your doctor’s office or the pharmacy to pay your share of
      costs. The money will come directly out of your HSA.
    </p>,
    ,
    <ul>
      <li>
        Once you pay your deductible, you’ll pay a percentage of the total cost
        (also called coinsurance) anytime you get care for a covered service.
      </li>
      <li>
        All the money in your HSA rolls over from year to year, and it’s yours
        even if you change health plans, jobs or retire.
      </li>
      <li>
        The money you put into your HSA, any interest you earn and even the
        money you take out to pay for health care is all tax-free.
      </li>
      <li>
        You can contribute up to $3,550 for individuals and $7,100 for families.
        <sup>1</sup>
      </li>
      <li>
        You can earn extra money for your HSA by doing healthy activities. Your
        employer will let you know how to participate.
      </li>
      <li>If you’re 55 or older you can contribute an extra $1,000 a year.</li>
      <li>
        You can use your Limited Purpose FSA to help pay for dental and vision
        care like fillings, braces, eyeglasses and contacts. You must have a
        Health Savings Account (HSA) to participate.
      </li>
    </ul>,
    ,
    <Footnote>
      1 For a full list of qualified expenses visit{' '}
      <Anchor to="https://irs.gov/pub502">irs.gov/pub502</Anchor>.
    </Footnote>,
    ,
  ],
  HRA1: [
    <p>
      Your plan comes with an HRA. The HRA is fully funded by your employer, so
      you don’t have to contribute anything from your paycheck. You can use
      money in the account to pay for qualified medical expenses like hospital
      visits, prescription drugs or copays for doctor visits.<sup>1</sup>
    </p>,
    ,
    <ul>
      <li>
        When you visit your doctor, you show your ID card. If you have money in
        your HRA, the payment is taken out automatically.
      </li>
      <li>
        Once you pay your deductible, you’ll pay a percentage of the total cost
        (also called coinsurance) anytime you get care for a covered service.
      </li>
      <li>
        If you don’t use all the money in your HRA one year, you can roll over
        the money to the next year if you stay in the plan. Your employer
        decides how much you can roll over each year.
      </li>
      <li>
        You can only use the HRA while you have this health plan. You can’t take
        the money with you if you change plans or leave your job.
      </li>
    </ul>,
    ,
    ,
    <Footnote>
      1 For a full list of qualified expenses visit{' '}
      <Anchor to="https://irs.gov/pub502">irs.gov/pub502</Anchor>.
    </Footnote>,
    ,
  ],
  HRA2: [
    <p>
      Your plan comes with an HRA. The HRA is fully funded by your employer, so
      you don’t have to contribute anything from your paycheck. You can use
      money in the account to pay for qualified medical expenses like hospital
      visits, prescription drugs or copays for doctor visit.<sup>1</sup>
    </p>,
    ,
    <ul>
      <li>
        When you visit your doctor, you show your ID card. If you have money in
        your HRA, the payment is taken out automatically.
      </li>
      <li>
        Once you pay your deductible, you’ll pay a percentage of the total cost
        (also called coinsurance) anytime you get care for a covered service.
      </li>
      <li>
        You can only use the HRA while you have this health plan. You can’t take
        the money with you if you change plans or leave your job.
      </li>
    </ul>,
    ,
    ,
    <Footnote>
      1 For a full list of qualified expenses visit{' '}
      <Anchor to="https://irs.gov/pub502">irs.gov/pub502</Anchor>.
    </Footnote>,
    ,
  ],
  HRA3: [
    <p>
      Your plan comes with an HRA. The HRA is fully funded by your employer, so
      you don’t have to contribute anything from your paycheck. You can use
      money in the account to pay for qualified medical expenses like hospital
      visits, prescription drugs or copays for doctor visit.<sup>1</sup>
    </p>,
    ,
    <ul>
      <li>
        When you visit your doctor, you show your ID card. If you have money in
        your HRA, the payment is taken out automatically.
      </li>
      <li>
        Once you pay your deductible, you’ll pay a percentage of the total cost
        (also called coinsurance) anytime you get care for a covered service.
      </li>
      <li>
        If you don’t use all the money in your HRA one year, you can roll over
        the money to the next year if you stay in the plan. Your employer
        decides how much you can roll over each year.
      </li>
      <li>
        You can only use the HRA while you have this health plan. You can’t take
        the money with you if you change plans or leave your job.
      </li>
      <li>
        You can earn extra money by doing healthy activities. Your employer will
        let you know how to participate.
      </li>
    </ul>,
    ,
    <Footnote>
      1 For a full list of qualified expenses visit{' '}
      <Anchor to="https://irs.gov/pub502">irs.gov/pub502</Anchor>.
    </Footnote>,
    ,
  ],
  HRA4: [
    <p>
      Your plan comes with an HRA. The HRA is fully funded by your employer, so
      you don’t have to contribute anything from your paycheck. You can use
      money in the account to pay for qualified medical expenses like hospital
      visits, prescription drugs or copays for doctor visit.<sup>1</sup>
    </p>,
    ,
    <ul>
      <li>
        When you visit your doctor, you show your ID card. If you have money in
        your HRA, the payment is taken out automatically.
      </li>
      <li>
        Once you pay your deductible, you’ll pay a percentage of the total cost
        (also called coinsurance) anytime you get care for a covered service.
      </li>
      <li>
        You can only use the HRA while you have this health plan. You can’t take
        the money with you if you change plans or leave your job.
      </li>
      <li>
        You can earn extra money by doing healthy activities. Your employer will
        let you know how to participate.
      </li>
    </ul>,
    ,
    <Footnote>
      1 For a full list of qualified expenses visit{' '}
      <Anchor to="https://irs.gov/pub502">irs.gov/pub502</Anchor>.
    </Footnote>
  ],
  'CS HRA1': [
    <p>
      Your plan comes with an HRA. The HRA is fully funded by your employer, so
      you don’t have to contribute anything from your paycheck. You can use
      money in the account to pay for qualified medical expenses like hospital
      visits, prescription drugs or copays for doctor visit.<sup>1</sup>
    </p>,
    ,
    <ul>
      <li>
        You’ll want to show your ID card when you visit the doctor. If you have
        money in your HRA, your copays, deductible and your percentage of the
        costs will be paid using your debit card.
      </li>
      <li>
        If you don’t have enough HRA funds to cover the cost, you’ll pay out of
        pocket for your copays, deductible and percentage of the cost until you
        reach your out-of-pocket maximum.
      </li>
      <li>
        Once you pay your deductible, you’ll pay a percentage of the total cost
        (also called coinsurance) anytime you get care for a covered service.
        Your plan will cover the rest.
      </li>
      <li>
        If you don’t use all the money in your HRA one year, you can roll over
        the money to the next year if you stay in the plan. Your employer
        decides how much you can roll over each year.
      </li>
      <li>
        You can only use the HRA while you have this health plan. You can’t take
        the money with you if you change plans or leave your job.
      </li>
      <li>
        You can earn extra money by doing healthy activities. Your employer will
        let you know how to participate.
      </li>
    </ul>,
    <Footnote>
      {' '}
      1 For a full list of qualified expenses visit{' '}
      <Anchor to="https://irs.gov/pub502">irs.gov/pub502</Anchor>.
    </Footnote>
  ],
  'CS HRA2': [
    <p>
      Your plan comes with an HRA. The HRA is fully funded by your employer, so
      you don’t have to contribute anything from your paycheck. You can use
      money in the account to pay for qualified medical expenses like hospital
      visits, prescription drugs or copays for doctor visit.<sup>1</sup>
    </p>,
    ,
    <ul>
      <li>
        You’ll want to show your ID card when you visit the doctor. If you have
        money in your HRA, your copays, deductible and your percentage of the
        costs will be paid.
      </li>
      <li>
        If you don’t have enough HRA funds to cover the cost, you’ll pay out of
        pocket for your copays, deductible and percentage of the cost until you
        reach your out-of-pocket maximum.
      </li>
      <li>
        Once you pay your deductible, you’ll pay a percentage of the total cost
        (also called coinsurance) anytime you get care for a covered service.
        Your plan will cover the rest.
      </li>
      <li>
        If you don’t use all the money in your HRA one year, you can roll over
        the money to the next year if you stay in the plan. Your employer
        decides how much you can roll over each year.
      </li>
      <li>
        You can only use the HRA while you have this health plan. You can’t take
        the money with you if you change plans or leave your job.
      </li>
      <li>
        You can earn extra money by doing healthy activities. Your employer will
        let you know how to participate.
      </li>
    </ul>,
    <Footnote>
      {' '}
      1 For a full list of qualified expenses visit{' '}
      <Anchor to="https://irs.gov/pub502">irs.gov/pub502</Anchor>.
    </Footnote>
  ],
  'CS HRA3': [
    <p>
      Your plan comes with an HRA. The HRA is fully funded by your employer, so
      you don’t have to contribute anything from your paycheck. You can use
      money in the account to pay for qualified medical expenses like hospital
      visits, prescription drugs or copays for doctor visit.<sup>1</sup>
    </p>,
    ,
    <ul>
      <li>
        You’ll want to show your ID card when you visit the doctor. If you have
        money in your HRA, your copays, deductible and your percentage of the
        costs will be paid using your debit card.
      </li>
      <li>
        If you don’t have enough HRA funds to cover the cost, you’ll pay out of
        pocket for your copays, deductible and percentage of the cost until you
        reach your out-of-pocket maximum.
      </li>
      <li>
        Once you pay your deductible, you’ll pay a percentage of the total cost
        (also called coinsurance) anytime you get care for a covered service.
        Your plan will cover the rest.
      </li>
      <li>
        You can only use the HRA while you have this health plan. You can’t take
        the money with you if you change plans or leave your job.
      </li>
      <li>
        You can earn extra money by doing healthy activities. Your employer will
        let you know how to participate.
      </li>
    </ul>,
    <Footnote>
      {' '}
      1 For a full list of qualified expenses visit{' '}
      <Anchor to="https://irs.gov/pub502">irs.gov/pub502</Anchor>.
    </Footnote>
  ],
  'CS HRA4': [
    <p>
      Your plan comes with an HRA. The HRA is fully funded by your employer, so
      you don’t have to contribute anything from your paycheck. You can use
      money in the account to pay for qualified medical expenses like hospital
      visits, prescription drugs or copays for doctor visit.<sup>1</sup>
    </p>,
    ,
    <ul>
      <li>
        You’ll want to show your ID card when you visit the doctor. If you have
        money in your HRA, your copays, deductible and your percentage of the
        costs will be paid using.
      </li>
      <li>
        If you don’t have enough HRA funds to cover the cost, you’ll pay out of
        pocket for your copays, deductible and percentage of the cost until you
        reach your out-of-pocket maximum.
      </li>
      <li>
        Once you pay your deductible, you’ll pay a percentage of the total cost
        (also called coinsurance) anytime you get care for a covered service.
        Your plan will cover the rest.
      </li>
      <li>
        You can only use the HRA while you have this health plan. You can’t take
        the money with you if you change plans or leave your job.
      </li>
      <li>
        You can earn extra money by doing healthy activities. Your employer will
        let you know how to participate.
      </li>
    </ul>,
    <Footnote>
      {' '}
      1 For a full list of qualified expenses visit{' '}
      <Anchor to="https://irs.gov/pub502">irs.gov/pub502</Anchor>.
    </Footnote>
  ],
  'CS HRA5': [
    <p>
      Your plan comes with an HRA. The HRA is fully funded by your employer, so
      you don’t have to contribute anything from your paycheck. You can use
      money in the account to pay for qualified medical expenses like hospital
      visits, prescription drugs or copays for doctor visit.<sup>1</sup>
    </p>,
    ,
    <ul>
      <li>
        You’ll want to show your ID card when you visit the doctor. If you have
        money in your HRA, your copays, deductible and your percentage of the
        costs will be paid using.
      </li>
      <li>
        If you don’t have enough HRA funds to cover the cost, you’ll pay out of
        pocket for your copays, deductible and percentage of the cost until you
        reach your out-of-pocket maximum.
      </li>
      <li>
        Once you pay your deductible, you’ll pay a percentage of the total cost
        (also called coinsurance) anytime you get care for a covered service.
        Your plan will cover the rest.
      </li>
      <li>
        You can only use the HRA while you have this health plan. You can’t take
        the money with you if you change plans or leave your job.
      </li>
    </ul>,
    ,
    <Footnote>
      {' '}
      1 For a full list of qualified expenses visit{' '}
      <Anchor to="https://irs.gov/pub502">irs.gov/pub502</Anchor>.
    </Footnote>
  ],
  'CS HRA6': [
    <p>
      Your plan comes with an HRA. The HRA is fully funded by your employer, so
      you don’t have to contribute anything from your paycheck. You can use
      money in the account to pay for qualified medical expenses like hospital
      visits, prescription drugs or copays for doctor visit.<sup>1</sup>
    </p>,
    ,
    <ul>
      <li>
        You’ll want to show your ID card when you visit the doctor. If you have
        money in your HRA, your copays, deductible and your percentage of the
        costs will be paid using your debit card.
      </li>
      <li>
        If you don’t have enough HRA funds to cover the cost, you’ll pay out of
        pocket for your copays, deductible and percentage of the cost until you
        reach your out-of-pocket maximum.
      </li>
      <li>
        Once you pay your deductible, you’ll pay a percentage of the total cost
        (also called coinsurance) anytime you get care for a covered service.
        Your plan will cover the rest.
      </li>
      <li>
        You can only use the HRA while you have this health plan. You can’t take
        the money with you if you change plans or leave your job.
      </li>
    </ul>,
    ,
    <Footnote>
      {' '}
      1 For a full list of qualified expenses visit{' '}
      <Anchor to="https://irs.gov/pub502">irs.gov/pub502</Anchor>.
    </Footnote>
  ],
  'DF HRA1': [
    <p>Health Reimbursement Account (HRA)</p>,
    <p>
      Your plan comes with an HRA. The HRA is fully funded by your employer, so
      you don’t have to contribute anything from your paycheck. You can use
      money in the account to pay for qualified medical expenses like hospital
      visits, prescription drugs or copays for doctor visit.<sup>1</sup>
    </p>,
    ,
    <ul>
      <li>
        You pay out of your own pocket for health care costs until you reach an
        “up front” portion of your deductible. After that, you can use money
        from your HRA to pay the rest of your deductible.
      </li>
      <li>
        Once you pay your deductible, you’ll pay a percentage of the total cost
        (also called coinsurance) anytime you get care for a covered service.
      </li>
      <li>
        If you don’t use all the money in your HRA one year, you can roll over
        the money to the next year if you stay in the plan. Your employer
        decides how much you can roll over each year.
      </li>
      <li>
        You can only use the HRA while you have this health plan. You can’t take
        the money with you if you change plans or leave your job.
      </li>
      <li>
        You can earn extra money by doing healthy activities. Your employer will
        let you know how to participate.
      </li>
    </ul>,
    ,
    ,
    <Footnote>
      1 For a full list of qualified expenses visit{' '}
      <Anchor to="https://irs.gov/pub502">irs.gov/pub502</Anchor>.
    </Footnote>
  ],
  'DF HRA2': [
    <p>Health Reimbursement Account (HRA)</p>,
    <p>
      Your plan comes with an HRA. The HRA is fully funded by your employer, so
      you don’t have to contribute anything from your paycheck. You can use
      money in the account to pay for qualified medical expenses like hospital
      visits, prescription drugs or copays for doctor visit.<sup>1</sup>
    </p>,
    ,
    <ul>
      <li>
        You pay out of your own pocket for health care costs until you reach an
        “up front” portion of your deductible. After that, you can use money
        from your HRA to pay the rest of your deductible.
      </li>
      <li>
        Once you pay your deductible, you’ll pay a percentage of the total cost
        (also called coinsurance) anytime you get care for a covered service.
      </li>
      <li>
        If you don’t use all the money in your HRA one year, you can roll over
        the money to the next year if you stay in the plan. Your employer
        decides how much you can roll over each year.
      </li>
      <li>
        You can only use the HRA while you have this health plan. You can’t take
        the money with you if you change plans or leave your job.
      </li>
      <li>
        You can earn extra money by doing healthy activities. Your employer will
        let you know how to participate.
      </li>
    </ul>,
    ,
    ,
    <Footnote>
      1 For a full list of qualified expenses visit{' '}
      <Anchor to="https://irs.gov/pub502">irs.gov/pub502</Anchor>.
    </Footnote>,
    ,
    <p>Health Reimbursement Account (HRA)</p>,
    <p>
      Your plan comes with an HRA. The HRA is fully funded by your employer, so
      you don’t have to contribute anything from your paycheck. You can use
      money in the account to pay for qualified medical expenses like hospital
      visits, prescription drugs or copays for doctor visit.<sup>1</sup>
    </p>,
    ,
    <ul>
      <li>
        You pay out of your own pocket for health care costs until you reach an
        “up front” portion of your deductible. After that, you can use money
        from your HRA to pay the rest of your deductible.
      </li>
      <li>
        Once you pay your deductible, you’ll pay a percentage of the total cost
        (also called coinsurance) anytime you get care for a covered service.
      </li>
      <li>
        If you don’t use all the money in your HRA one year, you can roll over
        the money to the next year if you stay in the plan. Your employer
        decides how much you can roll over each year.
      </li>
      <li>
        You can only use the HRA while you have this health plan. You can’t take
        the money with you if you change plans or leave your job.
      </li>
      <li>
        You can earn extra money by doing healthy activities. Your employer will
        let you know how to participate.
      </li>
    </ul>,
    ,
    ,
    <Footnote>
      1 For a full list of qualified expenses visit{' '}
      <Anchor to="https://irs.gov/pub502">irs.gov/pub502</Anchor>.
    </Footnote>,
    ,
  ],
  'DF HRA3': [
    <p>Health Reimbursement Account (HRA)</p>,
    <p>
      Your plan comes with an HRA. The HRA is fully funded by your employer, so
      you don’t have to contribute anything from your paycheck. You can use
      money in the account to pay for qualified medical expenses like hospital
      visits, prescription drugs or copays for doctor visit.<sup>1</sup>
    </p>,
    ,
    <ul>
      <li>
        You pay out of your own pocket for health care costs until you reach an
        “up front” portion of your deductible. After that, you can use money
        from your HRA to pay the rest of your deductible.
      </li>
      <li>
        Once you pay your deductible, you’ll pay a percentage of the total cost
        (also called coinsurance) anytime you get care for a covered service.
      </li>
      <li>
        You can only use the HRA while you have this health plan. You can’t take
        the money with you if you change plans or leave your job.
      </li>
    </ul>,
    ,
    <Footnote>
      1 For a full list of qualified expenses visit{' '}
      <Anchor to="https://irs.gov/pub502">irs.gov/pub502</Anchor>.
    </Footnote>
  ],
  'DF HRA4': [
    <p>Health Reimbursement Account (HRA)</p>,
    <p>
      Your plan comes with an HRA. The HRA is fully funded by your employer, so
      you don’t have to contribute anything from your paycheck. You can use
      money in the account to pay for qualified medical expenses like hospital
      visits, prescription drugs or copays for doctor visit.<sup>1</sup>
    </p>,
    ,
    <ul>
      <li>
        You pay out of your own pocket for health care costs until you reach an
        “up front” portion of your deductible. After that, you can use money
        from your HRA to pay the rest of your deductible.
      </li>
      <li>
        Once you pay your deductible, you’ll pay a percentage of the total cost
        (also called coinsurance) anytime you get care for a covered service.
      </li>
      <li>
        You can only use the HRA while you have this health plan. You can’t take
        the money with you if you change plans or leave your job.
      </li>
      <li>
        You can earn extra money by doing healthy activities. Your employer will
        let you know how to participate.
      </li>
    </ul>,
    ,
    <Footnote>
      1 For a full list of qualified expenses visit{' '}
      <Anchor to="https://irs.gov/pub502">irs.gov/pub502</Anchor>.
    </Footnote>
  ],
  'HRA Select1': [
    <p>
      Your plan comes with the option of an HRA. Your HRA is fully funded by
      your employer, so you don’t have to contribute anything from your
      paycheck.
    </p>,
    <ul>
      <li>
        Make sure to always show your ID card when you visit the doctor. If you
        have money in your HRA, the money to pay for health care costs like your
        copay and deductible will automatically be taken from your account.
      </li>
      <li>
        Once you pay your deductible, you’ll pay copays until you’ve reached
        your out-of-pocket maximum. When you reach this point, the plan will
        cover the rest.
      </li>
      <li>
        If you don’t use all your money in your HRA one year, you can roll over
        the money to the next year if you stay in the plan. Your employer
        decides how much you can roll over each year.
      </li>
      <li>
        You can only use your HRA while you have this health plan. You can’t
        take the money with you if you change plans or leave your job.
      </li>
      <li>
        You can earn extra money by doing healthy activities. Your employer will
        let you know how to participate.
      </li>
    </ul>
  ],
  'HRA Select2': [
    <p>
      Your plan comes with the option of an HRA. Your HRA is fully funded by
      your employer, so you don’t have to contribute anything from your
      paycheck.
    </p>,
    <ul>
      <li>
        Make sure to always show your ID card when you visit the doctor. If you
        have money in your HRA, the money to pay for health care costs like your
        copay and deductible will automatically be taken from your account.
      </li>
      <li>
        Once you pay your deductible, you’ll pay copays until you’ve reached
        your out-of-pocket maximum. When you reach this point, the plan will
        cover the rest.
      </li>
      <li>
        If you don’t use all your money in your HRA one year, you can roll over
        the money to the next year if you stay in the plan. Your employer
        decides how much you can roll over each year.
      </li>
      <li>
        You can only use your HRA while you have this health plan. You can’t
        take the money with you if you change plans or leave your job.
      </li>
    </ul>
  ],
  'HRA Select3': [
    <p>
      Your plan comes with the option of an HRA. Your HRA is fully funded by
      your employer, so you don’t have to contribute anything from your
      paycheck.
    </p>,
    <ul>
      <li>
        Make sure to always show your ID card when you visit the doctor. If you
        have money in your HRA, the money to pay for health care costs like your
        copay and deductible will automatically be taken from your account.
      </li>
      <li>
        Once you pay your deductible, you’ll pay copays until you’ve reached
        your out-of-pocket maximum. When you reach this point, the plan will
        cover the rest.
      </li>
      <li>
        You can only use your HRA while you have this health plan. You can’t
        take the money with you if you change plans or leave your job.
      </li>
    </ul>
  ],
  'HRA Select4': [
    <p>
      Your plan comes with the option of an HRA. Your HRA is fully funded by
      your employer, so you don’t have to contribute anything from your
      paycheck.
    </p>,
    <ul>
      <li>
        Make sure to always show your ID card when you visit the doctor. If you
        have money in your HRA, the money to pay for health care costs like your
        copay and deductible will automatically be taken from your account.
      </li>
      <li>
        Once you pay your deductible, you’ll pay copays until you’ve reached
        your out-of-pocket maximum. When you reach this point, the plan will
        cover the rest.
      </li>
      <li>
        You can only use your HRA while you have this health plan. You can’t
        take the money with you if you change plans or leave your job.
      </li>
      <li>
        You can earn extra money by doing healthy activities. Your employer will
        let you know how to participate.
      </li>
    </ul>
  ],
  'Split HRA1': [
    <p>
      Your plan comes with an HRA. The HRA is fully funded by your employer, so
      you don’t have to contribute anything from your paycheck. You can use
      money in the account to pay for qualified medical expenses like hospital
      visits, prescription drugs or copays for doctor visit.<sup>1</sup>
    </p>,
    <ul>
      <li>
        You’ll want to show your ID card when you visit the doctor. If you have
        money in your HRA, a percentage of the health care costs like your copay
        and deductible will automatically be taken from your account. You will
        pay the rest out of your own pocket.
      </li>
      <li>
        Once you pay your deductible, you’ll pay a percentage of the total cost
        (also called coinsurance) anytime you get care for a covered service.
      </li>
      <li>
        If you don’t use all the money in your HRA one year, you can roll over
        the money to the next year if you stay in the plan. Your employer
        decides how much you can roll over each year.
      </li>
      <li>
        You can only use the HRA while you have this health plan. You can’t take
        the money with you if you change plans or leave your job.
      </li>
      <li>
        You can earn extra money by doing healthy activities. Your employer will
        let you know how to participate.
      </li>
    </ul>,
    ,
    <Footnote>
      1 For a full list of qualified expenses visit{' '}
      <Anchor to="https://irs.gov/pub502">irs.gov/pub502</Anchor>.
    </Footnote>
  ],
  'Split HRA2': [
    <p>
      Your plan comes with an HRA. The HRA is fully funded by your employer, so
      you don’t have to contribute anything from your paycheck. You can use
      money in the account to pay for qualified medical expenses like hospital
      visits, prescription drugs or copays for doctor visit.<sup>1</sup>
    </p>,
    <ul>
      <li>
        You’ll want to show your ID card when you visit the doctor. If you have
        money in your HRA, a percentage of the health care costs like your copay
        and deductible will automatically be taken from your account. You will
        pay the rest out of your own pocket.
      </li>
      <li>
        Once you pay your deductible, you’ll pay a percentage of the total cost
        (also called coinsurance) anytime you get care for a covered service.
      </li>
      <li>
        If you don’t use all the money in your HRA one year, you can roll over
        the money to the next year if you stay in the plan. Your employer
        decides how much you can roll over each year.
      </li>
      <li>
        You can only use the HRA while you have this health plan. You can’t take
        the money with you if you change plans or leave your job.
      </li>
    </ul>,
    ,
    ,
    <Footnote>
      1 For a full list of qualified expenses visit{' '}
      <Anchor to="https://irs.gov/pub502">irs.gov/pub502</Anchor>.
    </Footnote>,
    ,
  ],
  'Split HRA3': [
    <p>
      Your plan comes with an HRA. The HRA is fully funded by your employer, so
      you don’t have to contribute anything from your paycheck. You can use
      money in the account to pay for qualified medical expenses like hospital
      visits, prescription drugs or copays for doctor visit.<sup>1</sup>
    </p>,
    <ul>
      <li>
        You’ll want to show your ID card when you visit the doctor. If you have
        money in your HRA, a percentage of the health care costs like your copay
        and deductible will automatically be taken from your account. You will
        pay the rest out of your own pocket.
      </li>
      <li>
        Once you pay your deductible, you’ll pay a percentage of the total cost
        (also called coinsurance) anytime you get care for a covered service.
      </li>
      <li>
        You can only use the HRA while you have this health plan. You can’t take
        the money with you if you change plans or leave your job.
      </li>
    </ul>,
    ,
    <Footnote>
      1 For a full list of qualified expenses visit{' '}
      <Anchor to="https://irs.gov/pub502">irs.gov/pub502</Anchor>.
    </Footnote>,
    ,
  ],
  'Split HRA4': [
    <p>
      Your plan comes with an HRA. The HRA is fully funded by your employer, so
      you don’t have to contribute anything from your paycheck. You can use
      money in the account to pay for qualified medical expenses like hospital
      visits, prescription drugs or copays for doctor visit.<sup>1</sup>
    </p>,
    <ul>
      <li>
        You’ll want to show your ID card when you visit the doctor. If you have
        money in your HRA, a percentage of the health care costs like your copay
        and deductible will automatically be taken from your account. You will
        pay the rest out of your own pocket.
      </li>
      <li>
        Once you pay your deductible, you’ll pay a percentage of the total cost
        (also called coinsurance) anytime you get care for a covered service.
      </li>
      <li>
        You can only use the HRA while you have this health plan. You can’t take
        the money with you if you change plans or leave your job.
      </li>
      <li>
        You can earn extra money by doing healthy activities. Your employer will
        let you know how to participate.
      </li>
    </ul>,
    ,
    <Footnote>
      1 For a full list of qualified expenses visit{' '}
      <Anchor to="https://irs.gov/pub502">irs.gov/pub502</Anchor>.
    </Footnote>,
    ,
  ],
  'MP HRA1': [
    <p>
      Your plan comes with an HRA. The HRA is fully funded by your employer, so
      you don’t have to contribute anything from your paycheck. You can use
      money in the account to pay for qualified medical expenses like hospital
      visits, prescription drugs or copays for doctor visit.<sup>1</sup>
    </p>,
    <ul>
      <li>
        You decide how you’ll pay for care — you can pay out of your own pocket
        and submit your claims for reimbursement or use your HRA debit card.
      </li>
      <li>
        Once you pay your deductible, you’ll pay a percentage of the total cost
        (also called coinsurance) anytime you get care for a covered service.
      </li>
      <li>
        If you don’t use all your money in your HRA one year, you can roll over
        the money to the next year if you stay in the plan. Your employer
        decides how much you can roll over each year.
      </li>
      <li>
        You can only use the HRA while you have this health plan. You can’t take
        the money with you if you change plans or leave your job.
      </li>
      <li>
        You can earn extra money by doing healthy activities. Your employer will
        let you know how to participate.
      </li>
    </ul>,
    ,
    <Footnote>
      1 For a full list of qualified expenses visit{' '}
      <Anchor to="https://irs.gov/pub502">irs.gov/pub502</Anchor>.
    </Footnote>
  ],
  'MP HRA2': [
    <p>
      Your plan comes with an HRA. The HRA is fully funded by your employer, so
      you don’t have to contribute anything from your paycheck. You can use
      money in the account to pay for qualified medical expenses like hospital
      visits, prescription drugs or copays for doctor visit.<sup>1</sup>
    </p>,
    <ul>
      <li>
        You decide how you’ll pay for care — you can pay out of your own pocket
        and submit your claims for reimbursement or use your HRA debit card.
      </li>
      <li>
        Once you pay your deductible, you’ll pay a percentage of the total cost
        (also called coinsurance) anytime you get care for a covered service.
      </li>
      <li>
        If you don’t use all your money in your HRA one year, you can roll over
        the money to the next year if you stay in the plan. Your employer
        decides how much you can roll over each year.
      </li>
      <li>
        You can only use the HRA while you have this health plan. You can’t take
        the money with you if you change plans or leave your job.
      </li>
    </ul>,
    ,
    ,
    <Footnote>
      1 For a full list of qualified expenses visit{' '}
      <Anchor to="https://irs.gov/pub502">irs.gov/pub502</Anchor>.
    </Footnote>,
    ,
  ],
  'MP HRA3': [
    <p>
      Your plan comes with an HRA. The HRA is fully funded by your employer, so
      you don’t have to contribute anything from your paycheck. You can use
      money in the account to pay for qualified medical expenses like hospital
      visits, prescription drugs or copays for doctor visit.<sup>1</sup>
    </p>,
    <ul>
      <li>
        You decide how you’ll pay for care — you can pay out of your own pocket
        and submit your claims for reimbursement or use your HRA debit card.
      </li>
      <li>
        Once you pay your deductible, you’ll pay a percentage of the total cost
        (also called coinsurance) anytime you get care for a covered service.
      </li>
      <li>
        You can only use the HRA while you have this health plan. You can’t take
        the money with you if you change plans or leave your job.
      </li>
    </ul>,
    ,
    ,
    <Footnote>
      1 For a full list of qualified expenses visit{' '}
      <Anchor to="https://irs.gov/pub502">irs.gov/pub502</Anchor>.
    </Footnote>,
    ,
  ],
  'MP HRA4': [
    <p>
      Your plan comes with an HRA. The HRA is fully funded by your employer, so
      you don’t have to contribute anything from your paycheck. You can use
      money in the account to pay for qualified medical expenses like hospital
      visits, prescription drugs or copays for doctor visit.<sup>1</sup>
    </p>,
    <ul>
      <li>
        You decide how you’ll pay for care — you can pay out of your own pocket
        and submit your claims for reimbursement or use your HRA debit card.
      </li>
      <li>
        Once you pay your deductible, you’ll pay a percentage of the total cost
        (also called coinsurance) anytime you get care for a covered service.
      </li>
      <li>
        You can only use the HRA while you have this health plan. You can’t take
        the money with you if you change plans or leave your job.
      </li>
      <li>
        You can earn extra money by doing healthy activities. Your employer will
        let you know how to participate.
      </li>
    </ul>,
    ,
    <Footnote>
      1 For a full list of qualified expenses visit{' '}
      <Anchor to="https://irs.gov/pub502">irs.gov/pub502</Anchor>.
    </Footnote>,
    ,
  ],
  'MP HRA5': [
    <p>
      Your plan comes with an HRA. The HRA is fully funded by your employer, so
      you don’t have to contribute anything from your paycheck. You can use
      money in the account to pay for qualified medical expenses like hospital
      visits, prescription drugs or copays for doctor visit.<sup>1</sup>
    </p>,
    ,
    <ul>
      <li>
        You will pay for care out of your own pocket and then submit your claims
        for reimbursement.
      </li>
      <li>
        Once you pay your deductible, you’ll pay a percentage of the total cost
        (also called coinsurance) anytime you get care for a covered service.
      </li>
      <li>
        If you don’t use all your money in your HRA one year, you can roll over
        the money to the next year if you stay in the plan. Your employer
        decides how much you can roll over each year.
      </li>
      <li>
        You can only use the HRA while you have this health plan. You can’t take
        the money with you if you change plans or leave your job.
      </li>
      <li>
        You can earn extra money by doing healthy activities. Your employer will
        let you know how to participate.
      </li>
    </ul>,
    ,
    <Footnote>
      1 For a full list of qualified expenses visit{' '}
      <Anchor to="https://irs.gov/pub502">irs.gov/pub502</Anchor>.
    </Footnote>,
    ,
  ],
  'MP HRA6': [
    <p>
      Your plan comes with an HRA. The HRA is fully funded by your employer, so
      you don’t have to contribute anything from your paycheck. You can use
      money in the account to pay for qualified medical expenses like hospital
      visits, prescription drugs or copays for doctor visit.<sup>1</sup>
    </p>,
    ,
    <ul>
      <li>
        You will pay for care out of your own pocket and then submit your claims
        for reimbursement.
      </li>
      <li>
        Once you pay your deductible, you’ll pay a percentage of the total cost
        (also called coinsurance) anytime you get care for a covered service.
      </li>
      <li>
        If you don’t use all your money in your HRA one year, you can roll over
        the money to the next year if you stay in the plan. Your employer
        decides how much you can roll over each year.
      </li>
      <li>
        You can only use the HRA while you have this health plan. You can’t take
        the money with you if you change plans or leave your job.
      </li>
    </ul>,
    ,
    <Footnote>
      1 For a full list of qualified expenses visit{' '}
      <Anchor to="https://irs.gov/pub502">irs.gov/pub502</Anchor>.
    </Footnote>,
    ,
  ],
  'MP HRA7': [
    <p>
      Your plan comes with an HRA. The HRA is fully funded by your employer, so
      you don’t have to contribute anything from your paycheck. You can use
      money in the account to pay for qualified medical expenses like hospital
      visits, prescription drugs or copays for doctor visit.<sup>1</sup>
    </p>,
    ,
    <ul>
      <li>
        You will pay for care out of your own pocket and then submit your claims
        for reimbursement.
      </li>
      <li>
        Once you pay your deductible, you’ll pay a percentage of the total cost
        (also called coinsurance) anytime you get care for a covered service.
      </li>
      <li>
        You can only use the HRA while you have this health plan. You can’t take
        the money with you if you change plans or leave your job.
      </li>
    </ul>,
    ,
    ,
    <Footnote>
      1 For a full list of qualified expenses visit{' '}
      <Anchor to="https://irs.gov/pub502">irs.gov/pub502</Anchor>.
    </Footnote>,
    ,
  ],
  'MP HRA8': [
    <p>
      Your plan comes with an HRA. The HRA is fully funded by your employer, so
      you don’t have to contribute anything from your paycheck. You can use
      money in the account to pay for qualified medical expenses like hospital
      visits, prescription drugs or copays for doctor visit.<sup>1</sup>
    </p>,
    ,
    <ul>
      <li>
        You will pay for care out of your own pocket and then submit your claims
        for reimbursement.
      </li>
      <li>
        Once you pay your deductible, you’ll pay a percentage of the total cost
        (also called coinsurance) anytime you get care for a covered service.
      </li>
      <li>
        You can only use the HRA while you have this health plan. You can’t take
        the money with you if you change plans or leave your job.
      </li>
      <li>
        You can earn extra money by doing healthy activities. Your employer will
        let you know how to participate.
      </li>
    </ul>,
    <Footnote>
      1 For a full list of qualified expenses visit{' '}
      <Anchor to="https://irs.gov/pub502">irs.gov/pub502</Anchor>.
    </Footnote>
  ],
  HIA1: [
    <p>
      With an HIA, you get rewarded for taking steps to improve your health and
      wellbeing. Your employer will put money into your account when you
      complete certain healthy activities. You can use money in the account to
      pay for qualified medical expenses like hospital visits, prescription
      drugs or copays for doctor visit.<sup>1</sup>
    </p>,
    <ul>
      <li>
        When you visit your doctor, show your ID card. If you have money in your
        HIA, the payment will be taken out of your account automatically.
      </li>
      <li>
        Once you pay your deductible, you’ll pay a percentage of the total cost
        (also called coinsurance) anytime you get care for a covered service.
      </li>
      <li>
        You can only use the HIA while you have this health plan. You can’t take
        the money with you if you change health plans or leave your job.
      </li>
      <li>
        If you don’t use all the money in your HIA one year, you can roll over
        that money to the next year if you stay in the plan.
      </li>
    </ul>,
    <Footnote>
      1 For a full list of qualified expenses visit{' '}
      <Anchor to="https://irs.gov/pub502">irs.gov/pub502</Anchor>.
    </Footnote>
  ],
  HIA2: [
    <p>
      With an HIA, you get rewarded for taking steps to improve your health and
      wellbeing. Your employer will put money into your account when you
      complete certain healthy activities. You can use money in the account to
      pay for qualified medical expenses like hospital visits, prescription
      drugs or copays for doctor visit.<sup>1</sup>
    </p>,
    <ul>
      <li>
        When you visit your doctor, show your ID card. If you have money in your
        HIA, the payment will be taken out of your account automatically.
      </li>
      <li>
        Once you pay your deductible, you’ll pay a percentage of the total cost
        (also called coinsurance) anytime you get care for a covered service.
      </li>
      <li>
        You can only use the HIA while you have this health plan. You can’t take
        the money with you if you change health plans or leave your job.
      </li>
    </ul>,
    <Footnote>
      1 For a full list of qualified expenses visit{' '}
      <Anchor to="https://irs.gov/pub502">irs.gov/pub502</Anchor>.
    </Footnote>
  ],
  ACO: [
    <p>
      The Accountable Care Organization (ACO) plan includes a network of
      doctors, hospitals and other health care professionals who focus on giving
      high-quality, coordinated care to Medicare patients.
    </p>,
    <ul>
      <li>
        You’ll choose a primary care doctor (PCP) from the plan for preventive
        care like checkups and screenings.
      </li>
      <li>
        When you want to see a specialist, like an orthopedic doctor or a
        cardiologist, you don’t need to visit your PCP first to get a referral.
        This can save you time and a copay.
      </li>
      <li>You’ll pay less for care if you see a doctor in the ACO plan.</li>
      <li>
        You’ll pay a bit more if you see a doctor in the full preferred provider
        organization (PPO) plan.
      </li>
      <li>
        You’ll pay a lot more if you see a doctor who isn’t in the ACO or PPO
        plans.
      </li>
      <li>
        It’s ideal for older members who have ongoing conditions like diabetes,
        asthma, high blood pressure as well as heart and joint conditions.
      </li>
    </ul>
  ],
  POS: [
    <p>
      With the Point of Service (POS) plan, you can see doctors in your plan and
      outside of your plan, giving you more flexibility and choices. The POS
      offers three different ways to get care:
    </p>,
    <ul>
      <li>
        You’ll pay less for care if you choose a primary care doctor (PCP) from
        the plan for preventive care like checkups and screenings.
      </li>
      <li>
        If you need care from a specialist, like an orthopedic doctor or a
        cardiologist, you’ll want to go through your PCP to get a referral. If
        you don’t get a referral, you’ll pay a bit more to see specialists.
      </li>
      <li>
        You’ll pay a bit more to see specialists or doctors in the plan without
        a referral
      </li>
      <li>
        You’ll pay even more when you go to doctors who aren’t in the plan.
      </li>
    </ul>
  ],
  'OA POS': [
    <p>
      Open Access Point of Service (POS) gives you more flexibility because you
      can see doctors in your plan and outside of your plan.
    </p>,
    <ul>
      <li>
        You’ll choose a primary care doctor (PCP) from the plan for preventive
        care like checkups and screenings.
      </li>
      <li>
        When you want to see a specialist, like an orthopedic doctor or a
        cardiologist, you don’t need to visit your PCP first to get a referral.
        This can save you time and a copay.
      </li>
      <li>You’ll pay less when you use doctors in the POS plan.</li>
      <li>You’ll pay more when you go to doctors who aren’t in the plan.</li>
    </ul>
  ],
  'AC PPO': [
    <p>
      Enjoy the power of three — choice, quality and savings. Choice Preferred
      Provider (PPO) Network gives you options when you need care. You can visit
      a doctor on each tier at any time, but you’ll pay more depending on which
      tier you use.
    </p>,
    <ul>
      <li>
        Tier 1: This is the lowest out-of-pocket cost when you use doctors who
        are part of the Choice PPO Network.
      </li>
      <li>
        Tier 2: You’ll get access to a broad range of doctors in our full PPO
        Network, so you have more choices.
      </li>
      <li>
        Tier 3: For more money, you’ll get the freedom to see doctors outside of
        the Choice PPO or PPO networks.
      </li>
    </ul>
  ],
  'VA POSAdv': [
    <p>
      With Point of Service (POS) AdvantageOne, you can see doctors in your plan
      and outside of your plan, giving you more flexibility and choices.
    </p>,
    <ul>
      <li>
        You’ll pay less for care if you choose a primary care doctor (PCP) from
        the plan for preventive care like checkups and screenings.
      </li>
      <li>
        If you need care from a specialist, like an orthopedic doctor or a
        cardiologist, you’ll want to go through your PCP to get a referral. If
        you don’t get a referral, you’ll pay a bit more to see the specialist.
      </li>
      <li>
        You can see doctors outside the plan, but you’ll save more money when
        you see doctors who are part of the POS plan.
      </li>
      <li>
        If you live in our Virginia service area, you’ll see primary care
        doctors and specialists covered by Anthem HealthKeepers, offered by
        HealthKeepers, Inc. If you live outside our Virginia service area,
        you’ll see primary care doctors and specialists covered by Blue Cross
        Blue Shield Global Core, offered by Anthem Blue Cross and Blue Shield.
      </li>
    </ul>
  ],
  'VA POSOA': [
    <p>
      The Point of Service (POS) Open Access plan lets you see doctors in your
      plan and outside of your plan, giving you more flexibility and choices.
    </p>,
    <ul>
      <li>
        You’ll choose a primary care doctor (PCP) from the plan for preventive
        care like checkups and screenings.
      </li>
      <li>
        When you want to see a specialist, like an orthopedic doctor or a
        cardiologist, you don’t need to visit your PCP first to get a referral.
        This can save you time and a copay.
      </li>
      <li>
        You can see doctors outside the plan, but you’ll save more money when
        you see doctors who are part of the POS plan.
      </li>
    </ul>
  ],
  'NV PathHMO': [
    <p>
      The Pathway Health Maintenance Organization (HMO) gives you a lower cost
      option while still offering broad coverage. It includes a wide range of
      doctors and health providers statewide.
    </p>,
    <ul>
      <li>
        You’ll need to choose a primary care doctor (PCP) who is part of the
        Pathway HMO Network for preventive care like checkups and screenings.
      </li>
      <li>
        You don’t need a referral from your PCP to get care from a specialist,
        like an orthopedic doctor or a cardiologist. This can save you time and
        a copay.
      </li>
      <li>
        You’ll have access to more than 32 acute care hospitals, 581 primary
        care doctors, 137 obstetricians/gynecologists (OB/GYNs) and 3,723
        specialty doctors across Nevada.
      </li>
      <li>
        When traveling out of state, you can access care through our Guest
        Membership/Away from Home Care program.
      </li>
    </ul>
  ],
  'NV PathPPO': [
    <p>
      Our Pathway Preferred Provider (PPO) Network ensures you have easy access
      to the doctors and specialists you need. You can go to almost any doctor
      or hospital — giving you more choices and flexibility.
    </p>,
    <ul>
      <li>
        You can choose a primary care doctor (PCP) from the plan for preventive
        care like checkups and screenings.
      </li>
      <li>
        You don’t need a referral from your PCP to see a specialist, like an
        orthopedic doctor or a cardiologist, as long as they are in your plan.
      </li>
      <li>
        You’ll pay less if you use a doctor who is part of the Pathway PPO
        Network.
      </li>
      <li>
        You’ll pay more if you go to a doctor who’s not part of the Pathway PPO
        Network.
      </li>
      <li>
        You’ll have access to more than 32 acute care hospitals, 581 primary
        care doctors, 137 obstetricians/gynecologists (OB/GYNs) and 3,723
        specialty doctors across Nevada.
      </li>
      <li>
        When traveling out of state, you can access care through our BlueCard(R)
        program.
      </li>
    </ul>
  ],
  Vivity: [
    <p>
      Vivity is a Health Maintenance Organization (HMO) plan that gives you the
      choice of great doctors and hospitals in your region of California. This
      care team is focused on bringing you high-quality care at an affordable
      cost.
    </p>,
    <ul>
      <li>
        You’ll choose a primary care doctor (PCP) from a hospital system in the
        plan for preventive care like checkups and screenings.
      </li>
      <li>
        Your PCP will refer you to a specialist or hospital for any additional
        care you need.
      </li>
      <li>
        When you get care, you’ll only have to pay a copay. Your Vivity plan
        will pay the rest.
      </li>
      <li>There aren’t any deductibles.</li>
    </ul>
  ],
  EPO1:[
    <p>
 This plan covers services from doctors and hospitals that are part of the Exclusive Provider Organization (EPO) plan.

    </p>,
    <ul>
      <li>
    Normally, you won’t have to go through your main doctor, if you need to see a specialist like an orthopedic doctor or a cardiologist.
      </li>
      <li>
    If you visit a doctor outside the plan, typically you’ll have limited benefits and pay more for care.
      </li>
    
    </ul>

  ],
  EPO2:[    <p>
    This plan covers services from doctors and hospitals that are part of the Exclusive Provider Organization (EPO) plan.
   
  </p>,
  <ul>
    <li>
       Normally, you will have to go through your main doctor, if you need to see a specialist like an orthopedic doctor or a cardiologist.
    </li>
    <li>
       If you visit a doctor outside the plan, typically you’ll have limited benefits and pay more for care.
    </li>
       
  </ul>]
  
};
